import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Phone, MessageCircle, MapPin, ShieldCheck, Loader2, Send,
  CheckCircle2, AlertTriangle, Mail,
} from 'lucide-react';
import { useContactInfo, getWhatsAppHref, getTelHref } from '../contexts/ContactInfoContext';
import { submitContactForm } from '../lib/api';
import { track } from '../lib/analytics';

/**
 * SOS conversion surfaces for the /urgent-medical-care pillar page.
 *
 *   • UrgentActionButtons   — Call + WhatsApp buttons for the hero.
 *   • UrgentSosForm         — "Where are you now?" selector + contact form.
 *   • UrgentStickyActionBar — mobile-only sticky Call / WhatsApp bar.
 *
 * Phone, WhatsApp and email come from the live contact-info API; if that
 * request fails we fall back to VietClinics' current, business-verified
 * details so an SOS visitor always has a working way to make contact.
 */

const FALLBACK = {
  hotlinePhone: '+84386492508',
  hotlineDisplay: '+84 38 649 2508',
  whatsappNumber: '84386492508',
  contactEmail: 'support@vietclinics.com',
};

const WA_MESSAGE = 'Hi VietClinics, I need urgent (non-emergency) medical help in Vietnam. Can you coordinate care for me?';

/** Resolve contact channels from API with a safe verified fallback. */
export const useUrgentContacts = () => {
  const { contactInfo } = useContactInfo();
  const info = {
    hotlinePhone: contactInfo?.hotlinePhone || FALLBACK.hotlinePhone,
    hotlineDisplay: contactInfo?.hotlineDisplay || FALLBACK.hotlineDisplay,
    whatsappNumber: contactInfo?.whatsappNumber || FALLBACK.whatsappNumber,
    contactEmail: contactInfo?.contactEmail || FALLBACK.contactEmail,
  };
  const telHref = getTelHref(info) || `tel:${FALLBACK.hotlinePhone}`;
  const whatsappHref = getWhatsAppHref(info, WA_MESSAGE) || `https://wa.me/${FALLBACK.whatsappNumber}`;
  const phoneDisplay = formatPhone(info.hotlineDisplay || info.hotlinePhone);
  return { telHref, whatsappHref, phoneDisplay, email: info.contactEmail };
};

function formatPhone(raw) {
  if (!raw) return FALLBACK.hotlineDisplay;
  const digits = raw.replace(/[^0-9]/g, '');
  // +84386492508 -> +84 38 649 2508
  if (digits === '84386492508') return '+84 38 649 2508';
  return raw;
}

export const trackCall = (placement) => track('urgent_call_click', { placement });
export const trackWhatsApp = (placement) => track('urgent_whatsapp_click', { placement });

/* ── Hero Call + WhatsApp buttons ─────────────────────────────────────── */
export const UrgentActionButtons = ({ placement = 'hero' }) => {
  const { telHref, whatsappHref, phoneDisplay } = useUrgentContacts();
  return (
    <div className="flex flex-col sm:flex-row gap-3" data-testid="urgent-action-buttons">
      <a
        href={telHref}
        onClick={() => trackCall(placement)}
        className="inline-flex items-center justify-center gap-2.5 text-white font-bold rounded-2xl px-6 py-4 transition-all hover:-translate-y-0.5"
        style={{ background: 'linear-gradient(135deg, #E5484D 0%, #C4342F 100%)', boxShadow: '0 12px 26px rgba(196,52,47,0.30)' }}
        data-testid="urgent-call-button"
      >
        <Phone className="h-5 w-5" />
        <span className="flex flex-col items-start leading-tight">
          <span>Call Urgent Support</span>
          <span className="text-[11px] font-semibold text-white/85">{phoneDisplay}</span>
        </span>
      </a>
      <a
        href={whatsappHref}
        target="_blank"
        rel="noreferrer"
        onClick={() => trackWhatsApp(placement)}
        className="inline-flex items-center justify-center gap-2.5 text-white font-bold rounded-2xl px-6 py-4 transition-all hover:-translate-y-0.5"
        style={{ background: 'linear-gradient(135deg, #25D366 0%, #1EBE57 100%)', boxShadow: '0 12px 26px rgba(37,211,102,0.28)' }}
        data-testid="urgent-whatsapp-button"
      >
        <MessageCircle className="h-5 w-5" />
        <span className="flex flex-col items-start leading-tight">
          <span>WhatsApp Urgent Support</span>
          <span className="text-[11px] font-semibold text-white/85">Chat with a coordinator</span>
        </span>
      </a>
    </div>
  );
};

/* ── Mobile sticky action bar ─────────────────────────────────────────── */
export const UrgentStickyActionBar = () => {
  const { telHref, whatsappHref } = useUrgentContacts();
  return (
    <div
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 px-3 py-2.5 flex gap-2.5"
      style={{ boxShadow: '0 -6px 20px rgba(15,42,85,0.10)', paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}
      data-testid="urgent-sticky-bar"
    >
      <a
        href={telHref}
        onClick={() => trackCall('sticky')}
        className="flex-1 inline-flex items-center justify-center gap-2 text-white font-bold rounded-xl py-3 text-sm"
        style={{ background: 'linear-gradient(135deg, #E5484D 0%, #C4342F 100%)' }}
        data-testid="urgent-sticky-call"
      >
        <Phone className="h-4 w-4" />
        Call
      </a>
      <a
        href={whatsappHref}
        target="_blank"
        rel="noreferrer"
        onClick={() => trackWhatsApp('sticky')}
        className="flex-1 inline-flex items-center justify-center gap-2 text-white font-bold rounded-xl py-3 text-sm"
        style={{ background: 'linear-gradient(135deg, #25D366 0%, #1EBE57 100%)' }}
        data-testid="urgent-sticky-whatsapp"
      >
        <MessageCircle className="h-4 w-4" />
        WhatsApp
      </a>
    </div>
  );
};

/* ── "Where are you now?" + contact form ──────────────────────────────── */
const LOCATIONS = [
  'Da Nang', 'Hoi An', 'Ho Chi Minh City', 'Hanoi', 'Nha Trang',
  'Phu Quoc', 'Sapa', 'Other city in Vietnam', 'Not in Vietnam yet',
];

const NEEDS = [
  'Hotel doctor visit', 'IV fluid therapy at hotel', 'Wound care & dressing',
  'English-speaking online consultation', 'Local clinic consultation',
  'Prescription / medication help', 'Not sure — need advice',
];

const inputCls =
  'w-full h-11 px-3.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 '
  + 'placeholder:text-slate-400 focus:outline-none focus:border-[#1FA7A1] focus:ring-2 '
  + 'focus:ring-[#1FA7A1]/15 transition-colors';

const Field = ({ label, htmlFor, children, className = '' }) => (
  <label htmlFor={htmlFor} className={`block ${className}`}>
    <span className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</span>
    {children}
  </label>
);

export const UrgentSosForm = () => {
  const { whatsappHref, telHref, phoneDisplay, email } = useUrgentContacts();
  const { search } = useLocation();
  // Preselect "Where are you now?" when a ?city= param is supplied (e.g. from
  // a city-page enquiry button). Only accept values we actually offer.
  const presetCity = (() => {
    const raw = new URLSearchParams(search).get('city');
    if (!raw) return '';
    return LOCATIONS.find((c) => c.toLowerCase() === raw.trim().toLowerCase()) || '';
  })();
  const [form, setForm] = useState({
    fullName: '', whatsapp: '', email: '', location: presetCity, need: '', message: '',
  });
  const [status, setStatus] = useState({ loading: false, sent: false, error: '' });

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.whatsapp || !form.location) {
      setStatus({ loading: false, sent: false, error: 'Please add your name, WhatsApp / phone number and where you are now.' });
      return;
    }
    setStatus({ loading: true, sent: false, error: '' });

    const message = [
      `URGENT (non-emergency) medical help request from ${form.fullName}.`,
      `Where they are now: ${form.location}`,
      form.need && `What they need: ${form.need}`,
      `WhatsApp / phone: ${form.whatsapp}`,
      form.email && `Email: ${form.email}`,
      form.message && `Details: ${form.message}`,
    ].filter(Boolean).join('\n');

    try {
      await submitContactForm({
        name: form.fullName,
        email: form.email || `wa-${form.whatsapp.replace(/[^0-9]/g, '')}@vietclinics-request.com`,
        phone: form.whatsapp,
        subject: `Urgent care request — ${form.location}${form.need ? ` · ${form.need}` : ''}`,
        message,
      });
      track('urgent_sos_submit', { location: form.location, need: form.need || 'unspecified', status: 'delivered' });
    } catch (err) {
      // Never block the conversion — log and fall back to success UI.
      // eslint-disable-next-line no-console
      console.warn('[UrgentSosForm] submission failed, falling back', err);
      track('urgent_sos_submit', { location: form.location, need: form.need || 'unspecified', status: 'fallback' });
    }
    setStatus({ loading: false, sent: true, error: '' });
  };

  return (
    <section id="urgent-sos" className="py-16 lg:py-20 bg-[#F7FAF9] scroll-mt-24" data-testid="urgent-sos-section">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Form */}
          <div
            className="lg:col-span-2 bg-white rounded-[28px] border border-slate-100 p-6 sm:p-9"
            style={{ boxShadow: '0 20px 60px rgba(15,42,85,0.08)' }}
          >
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">
              Urgent care enquiry
            </p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">
              Tell us where you are — a doctor comes to you
            </h2>
            <p className="text-slate-500 mt-2 text-sm sm:text-base leading-relaxed">
              Share a few details and our team will reply on WhatsApp to confirm your 24/7 doctor
              call-out. A licensed English-speaking doctor arrives at your address within 60 minutes.
            </p>

            {status.sent ? (
              <div className="text-center max-w-md mx-auto py-10" data-testid="urgent-sos-success">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-7 w-7 text-emerald-500" />
                </div>
                <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight">Request received</h3>
                <p className="text-slate-500 mt-2 leading-relaxed">
                  Thanks {form.fullName.split(' ')[0]}. Our coordinator will contact you shortly. For the
                  fastest reply, message or call us now.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackWhatsApp('success')}
                    className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold rounded-full px-6 py-3 transition-colors"
                    data-testid="urgent-sos-success-whatsapp"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Continue on WhatsApp
                  </a>
                  <a
                    href={telHref}
                    onClick={() => trackCall('success')}
                    className="inline-flex items-center justify-center gap-2 bg-white text-[#C4342F] font-bold rounded-full px-6 py-3 border-2 border-[#E5484D]/40 hover:bg-red-50 transition-colors"
                    data-testid="urgent-sos-success-call"
                  >
                    <Phone className="h-4 w-4" />
                    Call {phoneDisplay}
                  </a>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-testid="urgent-sos-form">
                <Field label="Full name *" htmlFor="sos-name">
                  <input id="sos-name" type="text" required value={form.fullName} onChange={onChange('fullName')}
                    placeholder="Your name" className={inputCls} data-testid="urgent-sos-input-name" />
                </Field>
                <Field label="WhatsApp / phone *" htmlFor="sos-whatsapp">
                  <input id="sos-whatsapp" type="tel" required value={form.whatsapp} onChange={onChange('whatsapp')}
                    placeholder="+1 ..." className={inputCls} data-testid="urgent-sos-input-whatsapp" />
                </Field>

                <Field label="Where are you now? *" htmlFor="sos-location">
                  <select id="sos-location" required value={form.location} onChange={onChange('location')}
                    className={inputCls} data-testid="urgent-sos-input-location">
                    <option value="">Select your location</option>
                    {LOCATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="What do you need?" htmlFor="sos-need">
                  <select id="sos-need" value={form.need} onChange={onChange('need')}
                    className={inputCls} data-testid="urgent-sos-input-need">
                    <option value="">Select (optional)</option>
                    {NEEDS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>

                <Field label="Email (optional)" htmlFor="sos-email" className="sm:col-span-2">
                  <input id="sos-email" type="email" value={form.email} onChange={onChange('email')}
                    placeholder="you@example.com" className={inputCls} data-testid="urgent-sos-input-email" />
                </Field>

                <Field label="Symptoms / details (optional)" htmlFor="sos-message" className="sm:col-span-2">
                  <textarea id="sos-message" rows={4} value={form.message} onChange={onChange('message')}
                    placeholder="Briefly describe what’s happening, your hotel / area, and any allergies or existing conditions."
                    className={`${inputCls} resize-none h-auto py-2.5`} data-testid="urgent-sos-input-message" />
                </Field>

                {status.error && (
                  <p className="sm:col-span-2 text-sm text-red-500 font-medium" data-testid="urgent-sos-error">{status.error}</p>
                )}

                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-1">
                  <p className="flex items-center gap-2 text-xs text-slate-500">
                    <ShieldCheck className="h-4 w-4 text-[#1FA7A1] flex-shrink-0" />
                    Coordinated by VietClinics — your details stay private.
                  </p>
                  <button type="submit" disabled={status.loading}
                    className="inline-flex items-center justify-center gap-2 bg-[#1FA7A1] hover:bg-[#178f8a] disabled:opacity-60 text-white font-bold rounded-full px-7 py-3.5 transition-all hover:-translate-y-0.5"
                    data-testid="urgent-sos-submit">
                    {status.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send urgent enquiry
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Direct-contact side card */}
          <aside className="lg:col-span-1 space-y-5" data-testid="urgent-sos-contact-card">
            <div className="rounded-[28px] p-7 text-white relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #0f2b55 0%, #1E5BA8 55%, #1FA7A1 100%)' }}>
              <p className="text-[11px] font-bold uppercase tracking-widest text-white/80 mb-3">Contact us directly</p>
              <div className="space-y-3">
                <a href={telHref} onClick={() => trackCall('card')}
                  className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-2xl px-4 py-3 transition-colors"
                  data-testid="urgent-sos-card-call">
                  <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0"><Phone className="h-4 w-4" /></span>
                  <span className="leading-tight">
                    <span className="block text-[11px] text-white/70">Call urgent support</span>
                    <span className="block font-bold">{phoneDisplay}</span>
                  </span>
                </a>
                <a href={whatsappHref} target="_blank" rel="noreferrer" onClick={() => trackWhatsApp('card')}
                  className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-2xl px-4 py-3 transition-colors"
                  data-testid="urgent-sos-card-whatsapp">
                  <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0"><MessageCircle className="h-4 w-4" /></span>
                  <span className="leading-tight">
                    <span className="block text-[11px] text-white/70">WhatsApp a coordinator</span>
                    <span className="block font-bold">Chat now</span>
                  </span>
                </a>
                <a href={`mailto:${email}`} onClick={() => track('urgent_email_click', { placement: 'card' })}
                  className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-2xl px-4 py-3 transition-colors"
                  data-testid="urgent-sos-card-email">
                  <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0"><Mail className="h-4 w-4" /></span>
                  <span className="leading-tight">
                    <span className="block text-[11px] text-white/70">Email enquiries</span>
                    <span className="block font-bold break-all">{email}</span>
                  </span>
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-[#FEC84B]/60 bg-[#FFFAEB] px-4 py-3 flex gap-3 items-start" role="note"
              data-testid="urgent-sos-emergency-note">
              <AlertTriangle className="h-5 w-5 text-[#B54708] flex-shrink-0 mt-0.5" />
              <p className="text-[12.5px] text-[#7A2E0E] leading-snug">
                <strong className="block text-[13px] mb-0.5">This is not an emergency line.</strong>
                For life-threatening emergencies in Vietnam, dial <strong>115</strong> for an ambulance or go to the
                nearest hospital emergency department. Messaging VietClinics does not replace emergency services.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};
